/**
 * promoOpenGate.ts — the CLIENT half of the List & Earn money gate.
 *
 * Asana 1218342457183573 items ② and ③. Server half is PR #869, on develop:
 * `GET /api/promo/list-and-earn/open` → `{ "open": boolean }`, unauthenticated,
 * counts never exposed. Read routes/listAndEarn.ts before changing anything
 * here; its docblock is the contract and this file is the other end of it.
 *
 * ── WHAT WAS WRONG WITHOUT THIS ─────────────────────────────────────────────
 * The guest interstitial (#855, app.js renderPromoSheet) advertises "Your first
 * $25 in fees is on us." to a logged-out visitor and consulted NO campaign
 * state at all. A guest could read that, register, and find every promo surface
 * dark — because all four signed-in surfaces gate on PROMO.source === "api",
 * which only lights when the server actually serves a promotion. The sheet was
 * the one surface making a money promise nothing had agreed to keep.
 *
 * ── 🔴 THE RULE, AND IT IS ONE RULE ─────────────────────────────────────────
 * RENDER ONLY ON 200 WITH `open === true`. EVERYTHING ELSE IS CLOSED.
 *
 * That is not defensive padding; each branch below is a measured outcome of the
 * real stack, named in routes/listAndEarn.ts:
 *
 *   200 {open:false}  the honest closed answer (inactive campaign, cap full).
 *   429               BY DESIGN, not abuse. The route burns TWO buckets per
 *                     request — promoOpenLimiter (60/15min) plus globalLimiter
 *                     (500/15min), whose `skip` does not exempt this path — and
 *                     `app.set('trust proxy', 2)` makes the key a real client
 *                     IP, so one office or CGNAT egress IP is ONE bucket shared
 *                     by every guest behind it.
 *   500               when the Upstash quota breaker calls disconnect() and
 *                     express-rate-limit rethrows (passOnStoreError is false).
 *   a body that is not this shape   an unparseable 200, or `open` carrying
 *                     anything but the boolean true.
 *   NO RESPONSE AT ALL   ioredis is configured `maxRetriesPerRequest: null`
 *                     with `enableOfflineQueue` left at its default true, so a
 *                     command issued during a Redis blip is QUEUED, not
 *                     rejected. Driven against a dead Redis: no response in six
 *                     seconds. NOTHING ON THE SERVER SIDE WILL EVER END THAT
 *                     WAIT — so the client's own timeout is the only thing that
 *                     can, and its expiry is a CLOSED answer like any other.
 *
 * 🪤 `open === true`, NEVER `!!body.open`. Same trap lib/listAndEarn.ts writes
 * up on the server side as `active !== true`: a truthy non-boolean — the string
 * "f", the number 1 — is exactly how an unknown value gets read as permission.
 * Over money, to an anonymous caller, "I could not tell" must read as no.
 *
 * ── WHY A PURE decide() AND A SEPARATE read() ───────────────────────────────
 * Mobile already solved this shape: index.tsx peeks the server on every feed
 * mount, never caches across sessions, and hands the answer to the pure
 * decideFeedPromo() in lib/interstitialGate.ts, whose whole eligibility test is
 * `input.peek?.eligible === true`. Splitting it the same way here is what makes
 * the timeout testable at all — a rule welded inside an async fetch can only be
 * driven through the network, and the no-response case is the one branch a
 * network mock is worst at reproducing.
 */

/** The one path. Unauthenticated by design — the caller has no account yet. */
export const PROMO_OPEN_PATH = "/api/promo/list-and-earn/open";

/**
 * How long the sheet is willing to wait before calling the campaign closed.
 *
 * 🔑 THREE THINGS FIX THIS NUMBER, AND IT IS NOT A ROUND-NUMBER GUESS:
 *
 * 1. IT IS THE HOUSE FIGURE FOR THIS EXACT JOB. mobile/lib/featureFlags.ts:131
 *    races a flag read — a boolean that decides whether a surface appears —
 *    against 3000 ms. This is the same job on the other client, so it gets the
 *    same budget rather than a new one invented here.
 *
 * 2. IT IS STRICTLY BELOW THE MEASURED HANG. The queued-command case was driven
 *    at no response in SIX seconds. A budget at or above that would never fire
 *    on the one outcome it exists for, and would ship as decoration.
 *
 * 3. AN INTERSTITIAL IS A GREETING OR IT IS AN INTERRUPTION. The sheet paints
 *    over the home page a guest has just landed on. Arriving late is worse than
 *    not arriving: by then they have started reading, and the offer lands as an
 *    obstacle rather than a welcome. Three seconds is the outer edge of "it was
 *    there when I got here".
 *
 * ⛔ AND DELIBERATELY NO RETRY, unlike featureFlags.ts's two attempts. That
 * retry exists because silently skipping a cache wipe is the harmful direction
 * there. Here the harmful direction is the opposite — showing an offer that is
 * closed — so a failed read already lands on the safe answer and a second
 * attempt buys nothing. It would also cost: this route burns two rate-limit
 * buckets per request against a key shared by every guest behind one egress IP,
 * so retrying makes the 429 above more likely for everyone else on that IP.
 */
export const PROMO_OPEN_TIMEOUT_MS = 3000;

/**
 * What actually came back. `no-response` covers the transport never answering —
 * a thrown fetch, and our own timeout expiring — because the client cannot tell
 * those apart and, more to the point, does not need to: both mean closed.
 */
export type PromoOpenOutcome =
  | { kind: "response"; status: number; body: unknown }
  | { kind: "no-response" };

interface PromoOpenWindow extends Window {
  /** Installed by bridgeScriptSource; reaches app.js's own gate. */
  __gbzPromoSheetOpen?: () => void;
}

/**
 * THE RULE, as a pure function. Drive it with every outcome — that is the point
 * of it being out here rather than inline in the read below.
 */
export function decidePromoOpen(outcome: PromoOpenOutcome | null | undefined): boolean {
  if (!outcome || outcome.kind !== "response") return false; /* timeout, or no transport */
  if (outcome.status !== 200) return false; /* 429, 500, and anything else */
  const body = outcome.body;
  if (!body || typeof body !== "object") return false; /* null, a string, a parse failure */
  return (body as { open?: unknown }).open === true; /* the boolean, and only the boolean */
}

/**
 * The network read, and nothing else — it decides nothing.
 *
 * 🪤 A RACE, NOT AN AbortSignal, AND THAT IS THE WHOLE POINT. The hazard this
 * budget exists for is a request that never settles. `AbortController` only
 * ends a wait that the transport agrees to end; a race ends it unconditionally,
 * which is the guarantee the contract actually needs. The pattern is lifted
 * from featureFlags.ts's fetchFlagResponseWithRetry for that reason.
 *
 * A late response is simply ignored, and can do no harm: the decision below is
 * ONE-WAY (see installPromoOpenGate), so nothing that arrives after the budget
 * has expired can reopen a sheet that has already been called closed.
 */
export async function readPromoOpen(
  fetchImpl: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  timeoutMs: number = PROMO_OPEN_TIMEOUT_MS,
): Promise<PromoOpenOutcome> {
  const attempt = async (): Promise<PromoOpenOutcome> => {
    let res: Response;
    try {
      res = await fetchImpl(PROMO_OPEN_PATH);
    } catch {
      return { kind: "no-response" }; /* offline, DNS, CORS, a refused socket */
    }
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null; /* a 200 whose body will not parse is not an open campaign */
    }
    return { kind: "response", status: res.status, body };
  };

  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<PromoOpenOutcome>((resolve) => {
    timer = setTimeout(() => resolve({ kind: "no-response" }), timeoutMs);
  });

  try {
    return await Promise.race([attempt(), expiry]);
  } finally {
    /* The page outlives this read; an uncleared 3-second timer would keep the
       event loop (and, in the suites, the test runner) busy for no reason. */
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * Read, decide, and tell app.js — but ONLY when the answer is yes.
 *
 * 🔴 THE BRIDGE IS ONE-WAY ON PURPOSE. app.js starts closed and this is the
 * only thing that can open it, so there is no path — no ordering, no late
 * response, no unexpected payload — where a wrong answer turns the sheet ON.
 * Every failure mode lands on "we never called the bridge", which is already
 * the correct end state. A two-way setter would need every caller to be right
 * about the negative case too; this needs them to be right about nothing.
 *
 * Returns the decision so the call site and the suites can read it.
 */
export async function installPromoOpenGate(
  win: PromoOpenWindow,
  fetchImpl: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  timeoutMs: number = PROMO_OPEN_TIMEOUT_MS,
): Promise<boolean> {
  const open = decidePromoOpen(await readPromoOpen(fetchImpl, timeoutMs));
  if (!open) return false;
  /* Absent bridge = the bridge script has not run; same posture as
     installPromo, which returns early rather than inventing a surface. */
  if (typeof win.__gbzPromoSheetOpen !== "function") return false;
  win.__gbzPromoSheetOpen();
  return true;
}
